<script setup lang="ts">
  import { AddressMeterPointDetails } from "@/api/model"
  import { ExternalRoute } from "@/constants/externalRoute"
  import { AddressFinderModel, AddressNotListed } from "@/types/AddressType"
  import { toTypedSchema } from "@vee-validate/zod"
  import { templateRef } from "@vueuse/core"
  import { useForm } from "vee-validate"
  import { computed, nextTick, ref, watch } from "vue"
  import { z } from "zod"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import { Combobox } from "@/components/ui/inputs/combobox"
  import { PostcodeFinder } from "@/components/ui/inputs/postcode-finder"
  import Link from "@/components/ui/link/Link.vue"

  type AddressListItem = { label: string; value: AddressFinderModel }

  const props = defineProps<{
    modelValue?: AddressFinderModel
    postcode?: string
    defaultPostcode?: string
    defaultAddress?: AddressFinderModel
    filterMpan?: boolean
  }>()

  const emits = defineEmits({
    "update:modelValue": (_?: AddressFinderModel) => true,
  })

  const formSchema = toTypedSchema(
    z.object({
      address: z
        .object({
          label: z
            .string({
              required_error: "Select your address from the list",
            })
            .min(1, "Select your address from the list"),
        })
        .default({ label: "" }),
    }),
  )

  const modalPostcodeValue = ref(props.postcode)

  watch(
    () => props.defaultPostcode,
    async (newValue) => {
      if (newValue) {
        modalPostcodeValue.value = props.defaultPostcode
        await postcodeFinder.value?.validateSubmitPostcode()
        if (props.defaultAddress === AddressNotListed) {
          emits("update:modelValue", AddressNotListed)
        } else if (props.defaultAddress) {
          modelAddressValue.value = addressItems.value?.find(
            (address) =>
              (address.value as AddressMeterPointDetails).fullAddress ===
              (props.defaultAddress as AddressMeterPointDetails).fullAddress,
          )
        }
      }
    },
  )

  const onUpdatePostcode = (postcode: string) => {
    if (postcode) {
      addressItems.value = undefined
      modelAddressValue.value = undefined
    }
  }

  const rawAddresses = ref<AddressMeterPointDetails[]>([])
  const addressItems = ref<AddressListItem[]>()
  const addressQueryError = ref<boolean>()
  const modelAddressValue = computed({
    get() {
      if (!props.modelValue) {
        return undefined
      }

      if (props.modelValue === AddressNotListed) {
        return {
          label: AddressNotListed,
          value: AddressNotListed,
        }
      }

      return {
        label: props.modelValue.fullAddress || "",
        value: props.modelValue,
      }
    },
    async set(value?: AddressListItem) {
      emits("update:modelValue", value?.value)
      if (value?.value) {
        await nextTick()
        await validate()
      }
    },
  })

  const enterAddressManually = () =>
    emits("update:modelValue", AddressNotListed)

  const mprnOnlyAddresses = computed(() =>
    rawAddresses.value.filter((a) => a.mprn && !a.mpan),
  )

  const setAddresses = (addressList: AddressMeterPointDetails[]) => {
    modelAddressValue.value = undefined
    rawAddresses.value = addressList
    const filtered = props.filterMpan
      ? addressList.filter((a) => a.mpan)
      : addressList
    addressItems.value = filtered.map((entry) => ({
      label: entry.fullAddress || "",
      value: entry,
    }))
  }

  const { validate } = useForm({
    validationSchema: formSchema,
  })

  const postcodeFinder = templateRef("postcodeFinder")
  const validatePostcode = () => postcodeFinder.value?.validate()

  const onSubmitPostcode = async () => {
    if (!addressItems.value?.length) {
      await postcodeFinder.value?.validateSubmitPostcode()
    }
    const postcodeValidation = await postcodeFinder.value?.validate()
    if (postcodeValidation?.valid) {
      validate()
    }
    return postcodeValidation
  }

  defineExpose({
    onSubmitPostcode,
    postcode: modalPostcodeValue,
    mprnOnlyAddresses,
    validatePostcode,
  })
</script>

<template>
  <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
    <PostcodeFinder
      ref="postcodeFinder"
      v-model="modalPostcodeValue"
      label="Postcode"
      name="postcode"
      placeholder="Enter your postcode"
      :default-value="postcode"
      @addresses="setAddresses($event)"
      @update:model-value="onUpdatePostcode"
      @query-error="addressQueryError = $event"
    />
    <div
      v-if="addressItems?.length"
      class="col-span-1 flex flex-col gap-y-2 sm:mt-8"
    >
      <Combobox
        v-model="modelAddressValue"
        autocomplete="off"
        data-testid="select-address"
        :items="addressItems"
        label="Select Address"
        :show-label="false"
        name="address"
        placeholder="Select Address"
      />
      <button
        type="button"
        class="self-start text-base text-primary-600 underline"
        data-testid="address-not-listed-link"
        @click="enterAddressManually"
      >
        Is your address not listed?
      </button>
    </div>
  </div>
  <Alert
    v-if="addressQueryError"
    variant="error"
    class="mt-4 w-auto text-left text-sm sm:text-base"
  >
    <AlertDescription>
      <slot name="query-error" :enter-address-manually="enterAddressManually">
        We couldn't load your postcode. Try again or
        <Link class="link" :to="ExternalRoute.getSupport">contact us</Link>
        for help.
      </slot>
    </AlertDescription>
  </Alert>
</template>
